
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useUserMode } from "@/stores/userModeStore";
import { useAuth } from "@/components/auth/AuthProvider";
import { Shield, User } from "lucide-react";

interface ModeSwitcherProps {
  className?: string;
  showLabels?: boolean;
}

export const ModeSwitcher = ({ className = "", showLabels = true }: ModeSwitcherProps) => {
  const { currentMode, setMode } = useUserMode();
  const { profile } = useAuth();
  
  // SECURITY: Only admins/managers can switch modes
  const canSwitchModes = profile?.role === 'admin' || profile?.role === 'manager';
  
  const handleModeChange = (isAdmin: boolean) => {
    // SECURITY: Prevent staff from switching to admin mode
    if (!canSwitchModes && isAdmin) {
      console.warn('Staff users cannot switch to admin mode');
      return;
    }
    
    setMode(isAdmin ? 'admin' : 'staff');
  };

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {showLabels && (
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Staff</span>
        </div>
      )}
      
      <Switch
        checked={currentMode === "admin"}
        onCheckedChange={handleModeChange}
        disabled={!canSwitchModes} // SECURITY FIX: Disable for staff users
        className="data-[state=checked]:bg-primary"
      />
      
      {showLabels && (
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-primary" />
          <span className="text-sm text-foreground">Admin</span>
        </div>
      )}
      
      <Badge 
        variant={currentMode === 'admin' ? 'default' : 'secondary'}
        className="ml-2"
      >
        {currentMode === 'admin' ? 'Admin Mode' : 'Staff Mode'}
        {!canSwitchModes && profile?.role === 'staff' && (
          <span className="ml-1 text-xs opacity-75">(Fixed)</span>
        )}
      </Badge>
    </div>
  );
};
